import { styled } from "goober";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { queries } from "../../shared/queries";
import type {
  BodyRef,
  HeaderPair,
  RequestLogRecord,
} from "../../shared/requestLogRecord";
import { useQuery } from "@rocicorp/zero/react";
import { executionContentPath } from "../lib/apiPaths";
import {
  decodeBodyBytes,
  decodeRequestBody,
  type DecodedRequestBody,
} from "../lib/decodeRequestBody";
import { useMakeUrl, routes } from "../lib/urls";
import { CodeBlock } from "./CodeBlock";
import { ScrollContainer, Table, Th, Thead } from "./CompactTable";
import { ConnectorLogo } from "./ConnectorLogo";
import { Icon } from "./Icon";
import { Link } from "./Link";
import { PressButton } from "./PressButton";
import { StatusBadge } from "./StatusBadge";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

type ParsedLine =
  | { kind: "request"; data: RequestLogRecord }
  | { kind: "dropped"; count: number }
  | { kind: "raw"; raw: string };

export function parseRequestLog(content: string): ParsedLine[] {
  const out: ParsedLine[] = [];
  for (const raw of content.split("\n")) {
    if (!raw.trim()) continue;
    try {
      const obj = JSON.parse(raw);
      if (typeof obj.dropped === "number") {
        out.push({ kind: "dropped", count: obj.dropped });
      } else if (
        typeof obj.method === "string" &&
        typeof obj.url === "string"
      ) {
        out.push({ kind: "request", data: obj as RequestLogRecord });
      } else {
        out.push({ kind: "raw", raw });
      }
    } catch {
      out.push({ kind: "raw", raw });
    }
  }
  return out;
}

function statusColor(status: number | null): string {
  if (status == null || status >= 500) return "var(--red-800)";
  if (status >= 400) return "var(--orange-800)";
  if (status >= 300) return "var(--sky-800)";
  return "var(--green-800)";
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms)) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatHeaders(headers: HeaderPair[] | undefined): string {
  if (!headers || headers.length === 0) return "(no headers)";
  return headers.map(([name, value]) => `${name}: ${value}`).join("\n");
}

/**
 * Exception-only badges: recording is on for every normal run, so a fully
 * recorded exchange is the unbadged default and blob rows with no rec fields
 * stay unbadged too.
 */
function requestBadge(
  r: RequestLogRecord
): { label: string; color: "teal" | "orange"; tooltip: string } | null {
  if (r.replayed) {
    return {
      label: "Replayed",
      color: "teal",
      tooltip:
        "Served from a recorded step-test fixture instead of the network.",
    };
  }
  if (
    r.rec === 1 &&
    (r.req_body_truncated ||
      r.res_body_truncated ||
      r.res_body_complete === false)
  ) {
    return {
      label: "Partial",
      color: "orange",
      tooltip: "Body capture was truncated or the response didn’t complete.",
    };
  }
  if (r.rec_skipped === "budget") {
    return {
      label: "Not recorded",
      color: "orange",
      tooltip: "Recording stopped at the per-run budget before this request.",
    };
  }
  return null;
}

const Tr = styled("tr")`
  &[data-expandable="true"] > td:first-child:hover {
    background: var(--alpha4);
  }
  &[data-expanded="true"] > td {
    background: var(--alpha4);
  }
`;

const TrailingTd = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  height: 36px;
  padding: 0 16px 0 8px;
  vertical-align: middle;
`;

const TrailingInner = styled("div")`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const ConnectorTrigger = styled(Link)`
  display: inline-flex;
`;

const RawCell = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  height: 36px;
  padding: 4px 16px;
  vertical-align: middle;
  font-family: var(--font-mono);
  font-size: 11px;
  line-height: 16px;
  color: var(--text-secondary);
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

const ChevronTd = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  border-right: 0.5px solid var(--border-alpha);
  height: 36px;
  width: 36px;
  padding: 0;
  text-align: center;
  vertical-align: middle;
  color: var(--text-secondary);
`;

const ChevronButton = styled(PressButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: var(--text-secondary);
`;

const DataTd = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  border-right: 0.5px solid var(--border-alpha);
  height: 36px;
  padding: 0;
`;

const StaticCell = styled("div")`
  display: flex;
  align-items: center;
  width: 100%;
  height: 36px;
  min-width: 0;
  padding: 0 16px;
`;

// The trailing badges cell draws no column divider, so its header cell must
// not either, or the header looks like it has an extra cell.
const BareTh = styled(Th)`
  border-right: none;
`;

const DetailTd = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  padding: 0;
  background: var(--alpha4);
  cursor: default;
`;

// Empty gutter in the chevron column so that cell's right divider continues
// down the full height of the expanded detail.
const DetailGutterTd = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  border-right: 0.5px solid var(--border-alpha);
  background: var(--alpha4);
`;

// Request and response each read as a group of cells; a single divider sits
// between the two groups (the last cell in a group drops its own rule so the
// group divider isn't doubled).
const DetailSectionGroup = styled("div")`
  & + & {
    border-top: 0.5px solid var(--border-alpha);
  }

  & > *:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled("div")`
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 16px;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const DetailContentCell = styled("div")`
  padding: 10px 16px;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const DetailNotes = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 16px;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const DetailNote = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
`;

const BinaryPlaceholder = styled("div")`
  padding: 8px 12px;
  border: 0.5px solid var(--border-alpha);
  border-radius: 8px;
  background: var(--alpha4);
  width: fit-content;
`;

function DecodedBody({ decoded }: { decoded: DecodedRequestBody }) {
  if (decoded.kind === "empty") {
    return (
      <Text variant="text-small" color="var(--text-secondary)">
        (empty body)
      </Text>
    );
  }
  if (decoded.kind === "binary") {
    return (
      <BinaryPlaceholder>
        <Text variant="text-small" color="var(--text-secondary)">
          binary{decoded.encoding ? ` (${decoded.encoding})` : ""},{" "}
          {decoded.byteLength.toLocaleString()} bytes
        </Text>
      </BinaryPlaceholder>
    );
  }
  return (
    <CodeBlock content={decoded.text} language={decoded.language} wrap bare />
  );
}

/**
 * A recorded body: older records carry it inline as base64; newer ones hold
 * an `[offset, length]` ref into the run's request-bodies blob, fetched with
 * a Range request only when the row is opened.
 */
function BodyBlock({
  executionId,
  base64,
  bodyRef,
  headers,
  cache,
}: {
  executionId: string | undefined;
  base64: string | undefined;
  bodyRef: BodyRef | undefined;
  headers: HeaderPair[] | undefined;
  cache: Map<string, DecodedRequestBody>;
}) {
  // Primitive deps: a re-fetched requests blob re-parses into fresh record
  // objects, and identity deps would refetch every expanded body for nothing.
  // The headers ride a ref for the same reason — for a given ref they don't
  // change.
  const [offset, length] = bodyRef ?? [-1, -1];
  const cacheKey =
    bodyRef && executionId != null
      ? `${executionId}:${offset}:${length}`
      : null;
  // Read the cache in the initializer (not an effect) so a body already
  // fetched on a prior expand of this row renders immediately on remount,
  // with no fetch and no "Loading…" flash.
  const [fetched, setFetched] = useState<
    | { kind: "loading" }
    | { kind: "failed" }
    | { kind: "done"; decoded: DecodedRequestBody }
  >(() => {
    const cached = cacheKey ? cache.get(cacheKey) : undefined;
    return cached ? { kind: "done", decoded: cached } : { kind: "loading" };
  });
  const headersRef = useRef(headers);
  useEffect(() => {
    headersRef.current = headers;
  });
  useEffect(() => {
    if (offset < 0) {
      let cancelled = false;
      void decodeRequestBody(base64, headersRef.current ?? []).then(
        (decoded) => {
          if (!cancelled) setFetched({ kind: "done", decoded });
        }
      );
      return () => {
        cancelled = true;
      };
    }
    if (!executionId) return;
    if (length === 0) {
      // oxlint-disable-next-line react/react-compiler -- EffectSetState: short-circuits to an empty decoded body inside the ranged request-body fetch
      setFetched({ kind: "done", decoded: { kind: "empty" } });
      return;
    }
    const cached = cacheKey ? cache.get(cacheKey) : undefined;
    if (cached) {
      setFetched({ kind: "done", decoded: cached });
      return;
    }
    const controller = new AbortController();
    setFetched({ kind: "loading" });
    void fetch(executionContentPath(executionId, "request-bodies"), {
      credentials: "include",
      headers: { Range: `bytes=${offset}-${offset + length - 1}` },
      signal: controller.signal,
    })
      .then(async (res) => {
        if (res.status !== 206) throw new Error(String(res.status));
        const bytes = new Uint8Array(await res.arrayBuffer());
        const decoded = await decodeBodyBytes(bytes, headersRef.current ?? []);
        if (!controller.signal.aborted) {
          if (cacheKey) cache.set(cacheKey, decoded);
          setFetched({ kind: "done", decoded });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setFetched({ kind: "failed" });
      });
    return () => controller.abort();
  }, [executionId, offset, length, cacheKey, cache, base64]);

  if (fetched.kind === "loading") {
    return (
      <Text variant="text-small" color="var(--text-secondary)">
        Loading…
      </Text>
    );
  }
  if (fetched.kind === "failed") {
    return (
      <Text variant="text-small" color="var(--text-secondary)">
        Couldn’t load the recorded body.
      </Text>
    );
  }
  return <DecodedBody decoded={fetched.decoded} />;
}

function DetailField({
  subject,
  detail,
  children,
}: {
  subject: string;
  detail: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <DetailLabel>
        <span>
          <Text variant="text-small-md" color="var(--text-primary)">
            {subject}
          </Text>{" "}
          <Text variant="text-small" color="var(--text-secondary)">
            {detail}
          </Text>
        </span>
      </DetailLabel>
      <DetailContentCell>{children}</DetailContentCell>
    </>
  );
}

function RequestDetail({
  data,
  executionId,
  cache,
}: {
  data: RequestLogRecord;
  executionId: string | undefined;
  cache: Map<string, DecodedRequestBody>;
}) {
  const truncated = data.req_body_truncated || data.res_body_truncated;
  const incomplete = data.res_body_complete === false;
  return (
    <>
      {(truncated || incomplete) && (
        <DetailNotes>
          {truncated && (
            <DetailNote>
              <Icon name="info" size={12} color="var(--orange-800)" />
              <Text variant="text-small" color="var(--orange-800)">
                Truncated at the 128 KiB recording cap
              </Text>
            </DetailNote>
          )}
          {incomplete && (
            <DetailNote>
              <Icon name="info" size={12} color="var(--orange-800)" />
              <Text variant="text-small" color="var(--orange-800)">
                Response did not complete
              </Text>
            </DetailNote>
          )}
        </DetailNotes>
      )}
      <DetailSectionGroup>
        <DetailField subject="Request" detail="headers">
          <CodeBlock content={formatHeaders(data.req_headers)} wrap bare />
        </DetailField>
        <DetailField subject="Request" detail="body">
          <BodyBlock
            executionId={executionId}
            base64={data.req_body}
            bodyRef={data.req_body_ref}
            headers={data.req_headers}
            cache={cache}
          />
        </DetailField>
      </DetailSectionGroup>
      <DetailSectionGroup>
        <DetailField subject="Response" detail="headers">
          <CodeBlock content={formatHeaders(data.res_headers)} wrap bare />
        </DetailField>
        <DetailField subject="Response" detail="body">
          <BodyBlock
            executionId={executionId}
            base64={data.res_body}
            bodyRef={data.res_body_ref}
            headers={data.res_headers}
            cache={cache}
          />
        </DetailField>
      </DetailSectionGroup>
    </>
  );
}

function RowCell({ children }: { children: React.ReactNode }) {
  return (
    <DataTd>
      <StaticCell>{children}</StaticCell>
    </DataTd>
  );
}

export function RequestsList({
  content,
  executionId,
  bottomRef,
  defaultExpandSingleRequest = false,
}: {
  content: string;
  executionId?: string;
  bottomRef?: React.Ref<HTMLDivElement>;
  defaultExpandSingleRequest?: boolean;
}) {
  const makeUrl = useMakeUrl();
  const [connectors] = useQuery(queries.connectedAppsLite());
  const connectorNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const connector of connectors ?? []) {
      map.set(connector.id, connector.name);
    }
    return map;
  }, [connectors]);
  // Bodies are immutable once recorded, so a fetched body survives
  // collapsing and re-expanding the same row instead of refetching.
  const [bodyCache] = useState(() => new Map<string, DecodedRequestBody>());
  const lines = useMemo(() => parseRequestLog(content), [content]);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => {
    if (!defaultExpandSingleRequest) return new Set();
    const requestIndices = lines.flatMap((line, i) =>
      line.kind === "request" ? [i] : []
    );
    return new Set(requestIndices.length === 1 ? requestIndices : []);
  });

  function toggleExpanded(i: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) {
        next.delete(i);
      } else {
        next.add(i);
      }
      return next;
    });
  }

  return (
    <ScrollContainer>
      <Table style={{ tableLayout: "fixed", width: "100%", minWidth: 600 }}>
        <colgroup>
          <col style={{ width: 36 }} />
          <col style={{ width: 88 }} />
          <col />
          <col style={{ width: 72 }} />
          <col style={{ width: 84 }} />
          <col style={{ width: 112 }} />
        </colgroup>
        <Thead>
          <tr>
            <Th />
            <Th>
              <Text variant="text-small" color="var(--text-secondary)">
                Method
              </Text>
            </Th>
            <Th>
              <Text variant="text-small" color="var(--text-secondary)">
                URL
              </Text>
            </Th>
            <Th>
              <Text variant="text-small" color="var(--text-secondary)">
                Status
              </Text>
            </Th>
            <Th>
              <Text variant="text-small" color="var(--text-secondary)">
                Duration
              </Text>
            </Th>
            <BareTh />
          </tr>
        </Thead>
        <tbody>
          {lines.map((line, i) => {
            if (line.kind === "raw") {
              return (
                // oxlint-disable-next-line react/no-array-index-key
                <tr key={i}>
                  <RawCell colSpan={6} title={line.raw}>
                    {line.raw}
                  </RawCell>
                </tr>
              );
            }
            if (line.kind === "dropped") {
              return (
                // oxlint-disable-next-line react/no-array-index-key
                <tr key={i}>
                  <RawCell colSpan={6}>
                    … {line.count} record{line.count === 1 ? "" : "s"} dropped
                    (buffer full)
                  </RawCell>
                </tr>
              );
            }
            const r = line.data;
            const connectorName = r.auth_connector_id
              ? connectorNameById.get(r.auth_connector_id)
              : undefined;
            const connectorId =
              r.auth_connector_id && connectorName !== undefined
                ? r.auth_connector_id
                : undefined;
            const authTooltip = !r.auth_connector
              ? "Connector credentials injected"
              : connectorName !== undefined
                ? `Credentials injected by the ${connectorName} connector`
                : "Credentials injected by a connector you can’t access";
            const badge = requestBadge(r);
            const expandable = r.rec === 1;
            const isExpanded = expandable && expanded.has(i);
            return (
              // oxlint-disable-next-line react/no-array-index-key
              <Fragment key={i}>
                <Tr
                  data-expandable={expandable || undefined}
                  data-expanded={isExpanded || undefined}
                >
                  <ChevronTd>
                    {expandable && (
                      <ChevronButton
                        onPress={() => toggleExpanded(i)}
                        aria-expanded={isExpanded}
                        aria-label={
                          isExpanded
                            ? "Collapse request details"
                            : "Expand request details"
                        }
                      >
                        <Icon
                          name={isExpanded ? "chevron-down" : "chevron-right"}
                          size={12}
                        />
                      </ChevronButton>
                    )}
                  </ChevronTd>
                  <RowCell>
                    <Text variant="code-small" color="var(--text-secondary)">
                      {r.method}
                    </Text>
                  </RowCell>
                  <RowCell>
                    <Text variant="code-small" ellipsis>
                      {r.url}
                    </Text>
                  </RowCell>
                  <RowCell>
                    <Tooltip content={r.error} hidden={!r.error}>
                      <Text variant="code-small" color={statusColor(r.status)}>
                        {r.status ?? "ERR"}
                      </Text>
                    </Tooltip>
                  </RowCell>
                  <RowCell>
                    <Text variant="code-small" color="var(--text-secondary)">
                      {formatDuration(r.duration_ms)}
                    </Text>
                  </RowCell>
                  <TrailingTd>
                    <TrailingInner>
                      {badge && (
                        <Tooltip content={badge.tooltip}>
                          <StatusBadge color={badge.color}>
                            {badge.label}
                          </StatusBadge>
                        </Tooltip>
                      )}
                      {r.auth_connector ? (
                        connectorId ? (
                          <ConnectorTrigger
                            to={makeUrl(routes.modal.connector, {
                              id: connectorId,
                            })}
                            title={authTooltip}
                          >
                            <ConnectorLogo
                              template={{
                                name: r.auth_connector,
                                identifier: r.auth_connector,
                              }}
                              size={12}
                            />
                          </ConnectorTrigger>
                        ) : (
                          <span
                            title={authTooltip}
                            style={{ display: "inline-flex" }}
                          >
                            <ConnectorLogo
                              template={{
                                name: r.auth_connector,
                                identifier: r.auth_connector,
                              }}
                              size={12}
                            />
                          </span>
                        )
                      ) : (
                        r.auth_applied && (
                          <span
                            title={authTooltip}
                            style={{ display: "inline-flex" }}
                          >
                            <Icon
                              name="shield-check"
                              size={12}
                              color="var(--green-800)"
                            />
                          </span>
                        )
                      )}
                    </TrailingInner>
                  </TrailingTd>
                </Tr>
                {isExpanded && (
                  <tr>
                    <DetailGutterTd />
                    <DetailTd colSpan={5}>
                      <RequestDetail
                        data={r}
                        executionId={executionId}
                        cache={bodyCache}
                      />
                    </DetailTd>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </Table>
      <div ref={bottomRef} />
    </ScrollContainer>
  );
}
